/**
 * Utility functions for transforming between Firebase timestamps and JavaScript Date objects
 */

/**
 * Converts a Firebase timestamp or numeric timestamp to a JavaScript Date object
 * 
 * @param {Object|number|Date} timestamp - The timestamp to convert
 *   Can be a Firestore timestamp (with seconds and nanoseconds),
 *   a numeric value in seconds or milliseconds,
 *   or an existing Date object
 * @returns {Date|null} - A JavaScript Date object or null if invalid input
 */
export const timestampToDate = (timestamp) => {
  // Return null if timestamp is null or undefined
  if (!timestamp) return null;
  
  // If it's already a Date object, return it
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a Firestore timestamp (has seconds property)
  if (timestamp.seconds !== undefined) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // If it's a number (either seconds or milliseconds)
  if (typeof timestamp === 'number') {
    // If it's in seconds (assuming timestamps after 1970 and before 2286)
    if (timestamp < 20000000000) {
      return new Date(timestamp * 1000);
    }
    // If it's in milliseconds
    return new Date(timestamp);
  }
  
  // If it's a string, try to parse it
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  // Return null for invalid inputs
  return null;
};

/**
 * Convert date fields in an object from Firebase timestamp format to JavaScript Date objects
 * 
 * @param {Object} data - Object containing potential date fields
 * @param {Array<string>} dateFields - Array of field names that might contain dates
 * @returns {Object} - A new object with converted date fields
 */
export const convertObjectDates = (data, dateFields = ['dob', 'createdAt', 'updatedAt', 'dateOfBirth', 'startDate', 'endDate']) => {
  if (!data) return data;
  
  const result = { ...data };
  
  dateFields.forEach(field => {
    if (result[field]) {
      result[field] = timestampToDate(result[field]);
    }
  });
  
  return result;
};

const firebaseTimestampTransformation = {
  timestampToDate,
  convertObjectDates
};

export default firebaseTimestampTransformation;
